import React, { useState, useEffect } from 'react';
import 'tailwindcss/tailwind.css';
import seedrandom from "seedrandom";

const numberPool = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 25, 50, 75, 100];


function canBeSolvedTooEasily(numbers, target) {
  if (numbers.includes(target)) return true;
  for (let i = 0; i < numbers.length; i++) {
    for (let j = 0; j < numbers.length; j++) {
      if (i === j) continue;
      const a = numbers[i];
      const b = numbers[j];
      if (a + b === target || a - b === target || b - a === target || a * b === target) return true;
      if ((b !== 0 && a / b === target) || (a !== 0 && b / a === target)) return true;
    }
  }
  return false;
}

function generateDeterministicPuzzle(seed) {
  const rng = seedrandom(seed);

  while (true) {
    const numbers = [];
    while (numbers.length < 6) {
      numbers.push(numberPool[Math.floor(rng() * numberPool.length)]);
    }

    let best = null;

    // Try 10 different builds and pick the simplest valid one
    for (let attempt = 0; attempt < 250; attempt++) {
      const { expression, result } = buildExpression(numbers, rng);

      if (result <= 0 || result > 500) continue;
      if (result < 10) continue;
      if (canBeSolvedTooEasily(numbers, result)) continue;

      const operationsCount = (expression.match(/[+\-*/]/g) || []).length;
      const exprLength = expression.length;

      if (
        !best ||
        operationsCount < best.ops ||
        (operationsCount === best.ops && exprLength < best.solution.length)
      ) {
        best = { numbers, target: result, solution: expression, ops: operationsCount };
      }
    }

    if (best) return { numbers: best.numbers, target: best.target, solution: best.solution };
  }
}


function buildExpression(numbers, rng) {
  const ops = ["+", "-", "*", "/"];
  let pool = [...numbers];
  let exprs = pool.map((n) => n.toString());

  while (pool.length > 1) {
    const i = Math.floor(rng() * pool.length);
    const j = Math.floor(rng() * pool.length);
    if (i === j) continue;

    const a = pool[i];
    const b = pool[j];
    const op = ops[Math.floor(rng() * ops.length)];

    let result;
    try {
      result = eval(`${a}${op}${b}`);
    } catch {
      continue;
    }

    if (!Number.isFinite(result)) continue;
    if (!Number.isInteger(result)) continue;

    // ✅ Skip useless operations
    if ((op === "+" || op === "-") && b === 0) continue;
    if ((op === "*" || op === "/") && b === 1) continue;

    // ✅ Smarter formatting
    let left = exprs[i];
    let right = exprs[j];

    // Add parentheses only when necessary
    if (op === "*" || op === "/") {
      if (/[+\-]/.test(left)) left = `(${left})`;
      if (/[+\-]/.test(right)) right = `(${right})`;
    }

    const newExpr = `${left}${op}${right}`;

    pool.splice(Math.max(i, j), 1);
    exprs.splice(Math.max(i, j), 1);
    pool.splice(Math.min(i, j), 1);
    exprs.splice(Math.min(i, j), 1);

    pool.push(result);
    exprs.push(newExpr);
  }

  return {
    expression: exprs[0].replace(/^\((.*)\)$/, "$1"), // remove outermost ()
    result: pool[0]
  };
}






export default function Maniardle() {
  const [puzzle, setPuzzle] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [status, setStatus] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [score, setScore] = useState(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [dailyMode, setDailyMode] = useState(true);
  const [showCopiedPopup, setShowCopiedPopup] = useState(false);
  const [usedCounts, setUsedCounts] = useState({});



  const loadPuzzle = (seed) => {
    const p = generateDeterministicPuzzle(seed);
    setPuzzle(p);
    setGuesses([]);       // ✅ Clear guess history
    setStatus('');        // ✅ Clear status message
    setStartTime(Date.now());
  };

  useEffect(() => {
    let seed;
    if (dailyMode) {
      const today = new Date();
      seed = today.getUTCFullYear() * 10000 + (today.getUTCMonth() + 1) * 100 + today.getUTCDate();
    } else {
      seed = Math.floor(Math.random() * 999999);
    }
    loadPuzzle(seed);
  }, [dailyMode]);

  const submitGuess = () => {
    if (!puzzle) return;

    const usedNumbers = currentGuess.split(/[^0-9]+/).filter(Boolean).map(Number);
    const counts = {};
    usedNumbers.forEach(num => counts[num] = (counts[num] || 0) + 1);

    for (let num in counts) {
      const available = puzzle.numbers.filter(n => n === parseInt(num, 10)).length;
      if (counts[num] > available) {
        setStatus(`❌ Invalid: You used ${num} ${counts[num]} times but only ${available} available!`);
        return;
      }
    }


    let result;
    try {
      result = eval(currentGuess);
    } catch {
      setStatus('Invalid expression');
      return;
    }

    const newGuesses = [...guesses, { guess: currentGuess, result }];
    setGuesses(newGuesses);
    setUsedCounts({});

    if (result === puzzle.target) {
      const finishTime = Date.now();
      const seconds = Math.floor((finishTime - startTime) / 1000);
      const points = Math.max(0, 100 - (newGuesses.length - 1) * 10 - Math.floor(seconds / 5));
      setScore(points);
      setStatus(`🎉 Correct! You solved it in ${newGuesses.length} tries and ${seconds}s. Score: ${points}\nSolution: ${puzzle.solution}`);
    } else if (newGuesses.length >= 6) {
      setStatus(`❌ Out of tries! Target was ${puzzle.target}. Solution: ${puzzle.solution}`);
    } else {
      setStatus(`Result: ${result} (Target: ${puzzle.target})`);
    }

    setCurrentGuess('');
  };
  const handleShare = () => {
    const today = new Date().toISOString().split("T")[0];

    if (!score) {
      // Copy a message saying "No result yet"
      navigator.clipboard.writeText(`🧮 Maniardle [${today}]\nNo result yet!\nmaniardle.kc3smw.net`).then(() => {
        setShowCopiedPopup(true);
        setTimeout(() => setShowCopiedPopup(false), 2000);
      });
      return;
    }

    const shareText =
      `🧮 Maniardle [${today}]\nTries: ${guesses.length}\nTime: ${Math.floor((Date.now() - startTime) / 1000)}s\nScore: ${score}`;

    if (navigator.share) {
      navigator.share({
        title: "Maniardle",
        text: `${shareText}\nhttps://maniardle.kc3smw.net`,
      }).catch(() => {
        // Fallback to clipboard
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(`${shareText}\nhttps://maniardle.kc3smw.net`).then(() => {
            setShowCopiedPopup(true);
            setTimeout(() => setShowCopiedPopup(false), 2000);
          });
        } else {
          alert("Copy not supported on this browser.");
        }
      });
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(`${shareText}\nhttps://maniardle.kc3smw.net`).then(() => {
        setShowCopiedPopup(true);
        setTimeout(() => setShowCopiedPopup(false), 2000);
      });
    } else {
      alert("Copy not supported on this browser.");
    }
  };



  return (
    <div className="relative flex flex-col items-center min-h-screen bg-gray-50 p-6">
      {showOverlay && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white shadow-2xl rounded-2xl p-8 max-w-lg text-center">
            <h1 className="text-5xl font-extrabold text-indigo-600 mb-4">Maniardle</h1>
            <p className="text-lg mb-2">A math puzzle game inspired by the British TV show Countdown.</p>
            <p className="text-gray-600 mb-4">Solve the puzzle using the numbers given. You have 6 tries. Your score depends on how fast and efficiently you solve it.</p>
            <p className="text-sm text-gray-500 mb-6">We do not collect personal data. Only scores will be tracked by username in future updates.</p>
            <button
              onClick={() => setShowOverlay(false)}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg text-lg font-semibold shadow"
            >
              Start Game
            </button>
          </div>
        </div>
      )}

      {puzzle && (
        <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-md">
          <h1 className="text-4xl font-extrabold mb-4 text-center text-indigo-600">Maniardle</h1>

          <div className="mb-4 text-center">
            <p className="text-lg">🎯 <strong className="text-indigo-700">Target:</strong> {puzzle.target}</p>

            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {puzzle.numbers.map((num, i) => {
                const disabled = usedCounts[i] >= 1; // Disable after one use

                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Keep keyboard open
                      if (!disabled) {
                        setCurrentGuess((prev) => prev + num);
                        setUsedCounts((prev) => ({ ...prev, [i]: (prev[i] || 0) + 1 }));
                      }
                    }}
                    className={`px-3 py-1 rounded-lg shadow-sm font-semibold ${
                      disabled
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-indigo-100 hover:bg-indigo-200 text-indigo-800"
                    }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>

          </div>

          <input
            type="tel"
            inputMode="numeric"
            value={currentGuess}
            onChange={(e) => setCurrentGuess(e.target.value)}
            className="border border-gray-300 p-3 mb-3 w-full text-lg rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
            placeholder="Enter expression (e.g. 50*10+3)"
          />
          <div className="grid grid-cols-6 gap-2 mb-3">
            {["+", "-", "*", "/", "(", ")"].map((symbol) => (
              <button
                key={symbol}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // ✅ Keeps keyboard open
                  setCurrentGuess((prev) => prev + symbol);
                }}
                className="bg-indigo-200 hover:bg-indigo-300 text-lg font-bold px-3 py-2 rounded-lg shadow"
              >
                {symbol}
              </button>
            ))}
          </div>

          <button
            onClick={submitGuess}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg w-full mb-4 font-bold shadow"
          >
            Submit
          </button>
          <button
            onClick={handleShare}
            disabled={!score}
            className={`px-4 py-2 rounded-lg w-full mb-4 font-bold shadow ${
              score
                ? "bg-purple-500 hover:bg-purple-600 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            📤 Share Result
          </button>

          {status && <p className="text-center text-lg font-bold mb-3">{status}</p>}

          <div className="bg-gray-100 p-3 rounded-lg shadow mb-4">
            <h2 className="text-lg font-semibold mb-2">Guess History</h2>
            {guesses.length === 0 && <p className="text-gray-500">No guesses yet.</p>}
            {guesses.map((g, i) => (
              <div key={i} className="flex justify-between border-b py-1">
                <span className="font-medium">Try {i + 1}:</span>
                <span>{g.guess} = {g.result}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col items-center gap-2">

            <p className="mt-2 font-bold">Play a Puzzle from X Days Ago:</p>
            <div className="grid grid-cols-6 gap-2">
              {Array.from({ length: 30 }, (_, i) => i + 1).map((daysAgo) => (
                <button
                  key={daysAgo}
                  onClick={() => {
                    const date = new Date();
                    date.setUTCDate(date.getUTCDate() - daysAgo);
                    const seed = date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
                    loadPuzzle(seed);
                    setStatus(`📅 Playing puzzle from ${daysAgo} days ago`);
                  }}
                  className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-3 py-1 rounded-lg font-semibold shadow-sm"
                >
                  {daysAgo}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {showCopiedPopup && (
        <div className="fixed bottom-6 right-6 bg-black text-white px-4 py-2 rounded-lg shadow-lg animate-fade-in-out">
          ✅ Copied to clipboard!
        </div>
      )}
    </div>
  );
}
